/**
 * inventoryPermissions.ts — Issue #228 / #345
 *
 * Inventory module permission helpers. Mirrors the existing helper pattern
 * (financePermissions / complaintPermissions / ...) so the admin sidebar can
 * hide the inventory entry from accounts without inventory read scope.
 *
 * The backend `/inventory/consumables*` controllers enforce the same strings
 * via `@RequirePermission('inventory.consumable', 'read'|'write')` and
 * `@RequirePermission('inventory.movement', 'read')` (Issue #345); UI guards
 * here are ergonomic only — the backend remains authoritative.
 */

import { hasAnyPermission, hasPermission } from './crmPermissions';

export { hasPermission, hasAnyPermission };

export const INVENTORY_PERMISSIONS = {
  CONSUMABLE_READ: 'inventory.consumable:read',
  CONSUMABLE_WRITE: 'inventory.consumable:write',
  MOVEMENT_READ: 'inventory.movement:read',
} as const;

export type InventoryPermission =
  (typeof INVENTORY_PERMISSIONS)[keyof typeof INVENTORY_PERMISSIONS];

export function canSeeInventoryMenu(granted: readonly string[]): boolean {
  return hasAnyPermission(granted, [
    INVENTORY_PERMISSIONS.CONSUMABLE_READ,
    INVENTORY_PERMISSIONS.MOVEMENT_READ,
  ]);
}

export function canSeeInventoryConsumables(granted: readonly string[]): boolean {
  return hasPermission(granted, INVENTORY_PERMISSIONS.CONSUMABLE_READ);
}

export function canSeeInventoryMovements(granted: readonly string[]): boolean {
  return hasPermission(granted, INVENTORY_PERMISSIONS.MOVEMENT_READ);
}
